import { Suspense, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "@suspensive/react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Globe, GlobeLock, Save, Trash2 } from "lucide-react";
import { HandbookEditorPanel } from "@/components/handbook/handbook-editor-panel";
import {
  handbookFormSchema,
  type HandbookFormValues,
} from "@/lib/validation/handbook-schema";
import {
  handbookByIdQueryOptions,
  useDeleteHandbook,
  usePublishHandbook,
  useUnpublishHandbook,
  useUpdateHandbook,
} from "@/hooks/use-handbook";
import { HandbookStatusBadge } from "@/components/handbook/handbook-status-badge";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";

function HandbookEditForm({ handbookId }: { handbookId: string }) {
  // 각주 영역이 본문 아래 추가되면서 page 길이가 viewport 를 넘는 케이스가 잦아짐.
  // 페이지 단 일반 스크롤로 갈음 (HandbookNewPage 와 동일 결정).
  const navigate = useNavigate();
  const { data: handbook } = useSuspenseQuery(
    handbookByIdQueryOptions(handbookId),
  );
  const updateMutation = useUpdateHandbook();
  const publishMutation = usePublishHandbook();
  const unpublishMutation = useUnpublishHandbook();
  const deleteMutation = useDeleteHandbook();

  const methods = useForm<HandbookFormValues>({
    resolver: zodResolver(handbookFormSchema),
    defaultValues: {
      slug: handbook.slug,
      title: handbook.title,
      content: handbook.content,
      excerpt: handbook.excerpt ?? "",
      tags: handbook.tags,
      subtitle: handbook.subtitle ?? "",
      thumbnailUrl: handbook.thumbnailUrl ?? "",
      parentIds: handbook.parentIds ?? [],
      relatedIds: handbook.relatedIds ?? [],
      aboutSlot: handbook.aboutSlot ?? null,
    },
  });

  // Reset form whenever the source-of-truth changes (e.g., after a publish toggle invalidates the cache).
  useEffect(() => {
    methods.reset({
      slug: handbook.slug,
      title: handbook.title,
      content: handbook.content,
      excerpt: handbook.excerpt ?? "",
      tags: handbook.tags,
      subtitle: handbook.subtitle ?? "",
      thumbnailUrl: handbook.thumbnailUrl ?? "",
      parentIds: handbook.parentIds ?? [],
      relatedIds: handbook.relatedIds ?? [],
      aboutSlot: handbook.aboutSlot ?? null,
      footnotes: handbook.footnotes ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handbook._id, handbook.updatedAt]);

  const save = methods.handleSubmit(async (values) => {
    // 슬롯 doc 은 위계/관계 없음 — 백엔드가 [aboutSlot != null && (parentIds 또는
    // relatedIds 비어있지 않음)] 조합을 거부하므로 안전망으로 빈 배열 강제.
    // UI 도 disable 되어 있지만 stale form state 가 남아있을 수 있어 여기서 한 번 더.
    const isSlotDoc = values.aboutSlot != null;
    await updateMutation.mutateAsync({
      handbookId,
      // slug 변경이 가능해진 백엔드 PR #916 에 맞춰 mutation 에 포함. 미전송이면
      // 백엔드는 undefined 로 받아 무변경 처리. 다른 doc 과 중복이면 ConflictException
      // 토스트로 노출 (useUpdateHandbook 의 onError extractMessage).
      slug: values.slug,
      title: values.title,
      content: values.content,
      excerpt: values.excerpt || undefined,
      tags: values.tags,
      // 빈 문자열은 null로 보내 명시적 clear.
      subtitle: values.subtitle === "" ? null : values.subtitle,
      thumbnailUrl: values.thumbnailUrl === "" ? null : values.thumbnailUrl,
      parentIds: isSlotDoc ? [] : values.parentIds,
      relatedIds: isSlotDoc ? [] : values.relatedIds,
      aboutSlot: values.aboutSlot,
      footnotes: values.footnotes,
    });
  });

  const togglePublish = async () => {
    if (handbook.status === "published") {
      unpublishMutation.mutate(handbookId);
    } else {
      publishMutation.mutate(handbookId);
    }
  };

  const onDelete = () => {
    if (!confirm(`'${handbook.title}' 페이지를 삭제하시겠습니까?`)) return;
    deleteMutation.mutate(handbookId, {
      onSuccess: () => navigate("/handbook", { replace: true }),
    });
  };

  const isPending =
    updateMutation.isPending ||
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    deleteMutation.isPending;

  return (
    <FormProvider {...methods}>
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center justify-between border-b px-4 py-2 md:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/handbook")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              목록
            </Button>
            <HandbookStatusBadge status={handbook.status} />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={save}
              disabled={isPending}
            >
              <Save className="mr-1 h-4 w-4" />
              저장
            </Button>
            <Button
              variant={handbook.status === "published" ? "outline" : "default"}
              size="sm"
              onClick={togglePublish}
              disabled={isPending}
            >
              {handbook.status === "published" ? (
                <>
                  <GlobeLock className="mr-1 h-4 w-4" />
                  발행 취소
                </>
              ) : (
                <>
                  <Globe className="mr-1 h-4 w-4" />
                  발행
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={isPending}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              삭제
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <HandbookEditorPanel currentHandbookId={handbookId} />
        </div>
      </div>
    </FormProvider>
  );
}

export default function HandbookEditPage() {
  const { handbookId } = useParams<{ handbookId: string }>();
  if (!handbookId) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        잘못된 경로입니다.
      </div>
    );
  }
  return (
    <ErrorBoundary fallback={SectionErrorFallback}>
      <Suspense fallback={<TableLoadingFallback columnCount={1} />}>
        <HandbookEditForm handbookId={handbookId} />
      </Suspense>
    </ErrorBoundary>
  );
}
